// Reward payload — the one JSON blob a reward carries, read and rendered.
//
// The payload describes what the player receives, and it is arbitrary JSON:
// the column is JSONB (migrations/0029_leaderboard_rewards.up.sql) and no Go
// path ever reads its keys — service, repository and the tier ladder all pass
// the json.RawMessage straight through.
//
// WHAT counts as a payload — an object or a list of items, never a bare scalar
// — is not decided here any more: it is the one reward rule in lib/reward.ts,
// which a help request type's `donor_reward` reads by too. This module is the
// leaderboards' side of it: the same verdict, worded with this field's own
// noun, plus how a payload is rendered in a cell and in a textarea.
//
// Both editors — the Rewards tab's inline drawer and the create form's reward
// rows — go through here, because the array ban used to live in each of them
// separately and that is exactly how they drifted apart from the backend.

import { parseReward, rewardProblemText } from "../../lib/reward";

/** Outcome of reading the payload textarea. `value` is undefined when blank. */
export type ParsedRewardPayload =
  | { ok: true; value: unknown }
  | { ok: false; error: string };

export function parseRewardPayload(raw: string): ParsedRewardPayload {
  const parsed = parseReward(raw);
  return parsed.ok
    ? parsed
    : { ok: false, error: rewardProblemText(parsed.problem, "Payload") };
}

/** One line of JSON for the table cell. A reward with no payload reads `{}`. */
export function rewardPayloadText(payload: unknown): string {
  if (payload === null || payload === undefined) return "{}";
  try {
    return JSON.stringify(payload) ?? "{}";
  } catch {
    return "{}";
  }
}

/**
 * Payload as the editor's textarea should start out.
 *
 * An empty object is what the API hands back for a reward created without a
 * payload, and the empty textarea says the same thing — so it stays empty. An
 * empty array does NOT: nothing produces it but an operator typing it, so it
 * survives the round-trip instead of quietly turning into {}.
 */
export function rewardPayloadToInput(payload: unknown): string {
  if (payload === null || payload === undefined) return "";
  if (
    typeof payload === "object" &&
    !Array.isArray(payload) &&
    Object.keys(payload).length === 0
  ) {
    return "";
  }
  try {
    return JSON.stringify(payload) ?? "";
  } catch {
    return "";
  }
}
